import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const currentLang = i18n.language;

  const switchLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLangMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return;
    }
    try {
      setFormStatus('success');
      setEmail('');
      setTimeout(() => setFormStatus('idle'), 3000);
    } catch (error) {
      console.error('Subscription error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal-900 font-body">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-charcoal-900/98 backdrop-blur-md border-b border-bronze-500/20' : 'bg-transparent'}`}>
        <div className="w-full px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-olive-500 to-olive-700 rounded flex items-center justify-center">
                <span className="text-white font-tactical font-bold text-lg">HY</span>
              </div>
              <div>
                <span className="text-white font-tactical font-semibold text-lg tracking-wide">{currentLang === 'zh' ? '薪瑀企業有限公司' : 'HSIN YU'}</span>
                <div className="h-0.5 w-full bg-gradient-to-r from-bronze-500 to-transparent"></div>
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-8">
              <a href="#about" className="text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 transition-colors tracking-wider whitespace-nowrap">{t('nav.about')}</a>
              <a href="#technology" className="text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 transition-colors tracking-wider whitespace-nowrap">{t('nav.technology')}</a>
              <a href="#services" className="text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 transition-colors tracking-wider whitespace-nowrap">{t('nav.aesthetics')}</a>
              <a href="#compliance" className="text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 transition-colors tracking-wider whitespace-nowrap">{t('nav.compliance')}</a>
              
              <div className="relative">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-1 text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 transition-colors tracking-wider whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-global-line"></i>
                  <span>{currentLang === 'zh' ? '中文' : 'EN'}</span>
                  <i className={`ri-arrow-down-s-line transition-transform ${langMenuOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {langMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-charcoal-800 rounded border border-olive-500/30 py-2 min-w-[120px] shadow-xl">
                    <button
                      onClick={() => switchLanguage('zh')}
                      className={`w-full px-4 py-2 text-left text-sm font-tactical hover:bg-olive-500/20 transition-colors cursor-pointer ${currentLang === 'zh' ? 'text-bronze-500 font-semibold' : 'text-charcoal-200'}`}
                    >
                      中文
                    </button>
                    <button
                      onClick={() => switchLanguage('en')}
                      className={`w-full px-4 py-2 text-left text-sm font-tactical hover:bg-olive-500/20 transition-colors cursor-pointer ${currentLang === 'en' ? 'text-bronze-500 font-semibold' : 'text-charcoal-200'}`}
                    >
                      English
                    </button>
                  </div>
                )}
              </div>

              <a href="#contact" className="px-6 py-2.5 bg-gradient-to-r from-bronze-500 to-bronze-600 text-charcoal-900 text-sm font-tactical font-bold rounded tracking-wider hover:from-bronze-400 hover:to-bronze-500 transition-all shadow-lg shadow-bronze-500/20 whitespace-nowrap cursor-pointer">{t('nav.contact')}</a>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-charcoal-200"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {isMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-3 bg-charcoal-800/95 rounded-lg p-4 border border-olive-500/30">
              <a href="#about" className="block text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 whitespace-nowrap py-2">{t('nav.about')}</a>
              <a href="#technology" className="block text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 whitespace-nowrap py-2">{t('nav.technology')}</a>
              <a href="#services" className="block text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 whitespace-nowrap py-2">{t('nav.aesthetics')}</a>
              <a href="#compliance" className="block text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 whitespace-nowrap py-2">{t('nav.compliance')}</a>
              <a href="#contact" className="block text-sm font-tactical font-medium text-charcoal-200 hover:text-bronze-500 whitespace-nowrap py-2">{t('nav.contact')}</a>
              <div className="border-t border-olive-500/30 pt-3 mt-3">
                <p className="text-xs text-charcoal-200 mb-2 font-tactical">{currentLang === 'zh' ? '語言' : 'Language'}</p>
                <div className="flex space-x-2">
                  <button onClick={() => switchLanguage('zh')} className={`px-3 py-1 text-sm font-tactical rounded cursor-pointer ${currentLang === 'zh' ? 'bg-bronze-500 text-charcoal-900' : 'bg-charcoal-700 text-charcoal-200'}`}>中文</button>
                  <button onClick={() => switchLanguage('en')} className={`px-3 py-1 text-sm font-tactical rounded cursor-pointer ${currentLang === 'en' ? 'bg-bronze-500 text-charcoal-900' : 'bg-charcoal-700 text-charcoal-200'}`}>English</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/8284752dd76c7d6fc4e578fe3b1ffb0a.jpg"
            alt="Precision Components"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/90 via-charcoal-900/75 to-charcoal-900"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiMxYTFhMWEiLz48cmVjdCB3aWR0aD0iMSIgaGVpZ2h0PSIxIiBmaWxsPSIjMjIyIi8+PC9zdmc+')] opacity-40"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-32 left-8 w-px h-32 bg-gradient-to-b from-transparent via-bronze-500 to-transparent"></div>
        <div className="absolute top-32 right-8 w-px h-32 bg-gradient-to-b from-transparent via-olive-500 to-transparent"></div>

        <div className="relative z-10 w-full px-6 lg:px-12 pt-32 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-3 mb-8">
                  <div className="h-px w-12 bg-bronze-500"></div>
                  <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">SINCE 2004</span>
                </div>

                <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-tight tracking-tight">
                  <span className="block mb-2" style={{ color: '#FFFFFF' }}>{t('hero.title1')}</span>
                  <span className="block text-bronze-500">{t('hero.title2')}</span>
                </h1>

                <p className="text-charcoal-200 text-base md:text-lg mb-10 max-w-xl leading-loose font-body">
                  {t('hero.description')}
                </p>

                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                  <a href="#about" className="group px-8 py-4 bg-bronze-500 text-charcoal-900 text-sm font-tactical font-bold tracking-wider hover:bg-bronze-400 transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2">
                    <span>{t('hero.learnMore')}</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                  </a>
                  <a href="#contact" className="px-8 py-4 border-2 border-olive-500 text-olive-400 text-sm font-tactical font-bold tracking-wider hover:bg-olive-500 hover:text-charcoal-900 transition-all whitespace-nowrap cursor-pointer">{t('hero.contactUs')}</a>
                </div>
              </div>

              {/* Technical Specs Card */}
              <div className="hidden lg:block">
                <div className="bg-charcoal-800/80 backdrop-blur-sm border-l-2 border-bronze-500 p-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="border-b border-charcoal-700 pb-4">
                      <div className="text-4xl font-display font-bold text-bronze-500">20+</div>
                      <div className="text-xs font-tactical text-charcoal-300 tracking-wider mt-1">{t('about.stat.experience')}</div>
                    </div>
                    <div className="border-b border-charcoal-700 pb-4">
                      <div className="text-4xl font-display font-bold text-olive-400">3</div>
                      <div className="text-xs font-tactical text-charcoal-300 tracking-wider mt-1">{t('about.stat.series')}</div>
                    </div>
                    <div className="pb-4">
                      <div className="text-4xl font-display font-bold text-white">HV600</div>
                      <div className="text-xs font-tactical text-charcoal-300 tracking-wider mt-1">MAX HARDNESS</div>
                    </div>
                    <div className="pb-4">
                      <div className="text-4xl font-display font-bold text-bronze-500">100%</div>
                      <div className="text-xs font-tactical text-charcoal-300 tracking-wider mt-1">{t('about.stat.traceability')}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2">
          <span className="text-xs font-tactical text-charcoal-300 tracking-widest">SCROLL</span>
          <div className="w-px h-8 bg-gradient-to-b from-bronze-500 to-transparent animate-pulse"></div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-charcoal-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/382a3f4e2dacfc6e9d3e92d1215e0f05.jpeg"
            alt="Metal Background"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-olive-500/50 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-3 mb-6">
                <div className="h-px w-12 bg-bronze-500"></div>
                <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">{t('about.subtitle')}</span>
              </div>

              <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-8 leading-relaxed">
                <span className="block mb-4">{t('about.title1')}</span>
                <span className="block mt-2 text-olive-400">{t('about.title2')}</span>
              </h2>

              <p className="text-charcoal-200 text-base leading-relaxed mb-8">
                {t('about.description')}
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <a href="#technology" className="px-6 py-3 bg-olive-500 text-charcoal-900 text-sm font-tactical font-bold tracking-wider hover:bg-olive-400 transition-all whitespace-nowrap cursor-pointer">{t('about.viewTechnology')}</a>
                <a href="#compliance" className="px-6 py-3 border border-charcoal-600 text-charcoal-200 text-sm font-tactical font-medium tracking-wider hover:border-bronze-500 hover:text-bronze-500 transition-all whitespace-nowrap cursor-pointer">{t('about.qualityCompliance')}</a>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-charcoal-800/80 backdrop-blur-sm border border-charcoal-700 p-6 hover:border-bronze-500/50 transition-colors">
                <div className="text-5xl font-display font-bold text-bronze-500 mb-2">2004</div>
                <div className="text-sm font-tactical text-charcoal-300 tracking-wider">{t('about.stat.founded')}</div>
              </div>
              <div className="bg-charcoal-800/80 backdrop-blur-sm border border-charcoal-700 p-6 hover:border-bronze-500/50 transition-colors">
                <div className="text-5xl font-display font-bold text-olive-400 mb-2">20+</div>
                <div className="text-sm font-tactical text-charcoal-300 tracking-wider">{t('about.stat.experience')}</div>
              </div>
              <div className="bg-charcoal-800/80 backdrop-blur-sm border border-charcoal-700 p-6 hover:border-bronze-500/50 transition-colors">
                <div className="text-5xl font-display font-bold text-white mb-2">3</div>
                <div className="text-sm font-tactical text-charcoal-300 tracking-wider">{t('about.stat.series')}</div>
              </div>
              <div className="bg-charcoal-800/80 backdrop-blur-sm border border-charcoal-700 p-6 hover:border-bronze-500/50 transition-colors">
                <div className="text-5xl font-display font-bold text-bronze-500 mb-2">100%</div>
                <div className="text-sm font-tactical text-charcoal-300 tracking-wider">{t('about.stat.traceability')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-24 bg-charcoal-800 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-bronze-500/30 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 gap-6">
            <div>
              <div className="inline-flex items-center space-x-3 mb-4">
                <div className="h-px w-12 bg-bronze-500"></div>
                <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">CERTIFICATIONS</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white">{t('cert.title')}</h2>
            </div>
            <p className="text-sm text-charcoal-200 max-w-md font-body">{t('cert.description')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* ISO 9001 */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 relative overflow-hidden hover:border-bronze-500/50 transition-all duration-300">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-bronze-500 to-olive-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="absolute top-4 right-4">
                <span className="px-2 py-1 bg-olive-500 text-charcoal-900 text-[10px] font-tactical font-bold tracking-wider">{t('cert.certified')}</span>
              </div>
              <div className="w-16 h-16 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mb-6">
                <i className="ri-award-line text-3xl text-bronze-500"></i>
              </div>
              <h3 className="text-xl font-display font-bold text-white mb-2">{t('cert.iso9001')}</h3>
              <p className="text-sm text-charcoal-200 font-body">{t('cert.iso9001.desc')}</p>
            </div>

            {/* ISO 14001 */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 relative overflow-hidden hover:border-bronze-500/50 transition-all duration-300">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-bronze-500 to-olive-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="absolute top-4 right-4">
                <span className="px-2 py-1 bg-olive-500 text-charcoal-900 text-[10px] font-tactical font-bold tracking-wider">{t('cert.certified')}</span>
              </div>
              <div className="w-16 h-16 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-3xl text-bronze-500"></i>
              </div>
              <h3 className="text-xl font-display font-bold text-white mb-2">{t('cert.iso14001')}</h3>
              <p className="text-sm text-charcoal-200 font-body">{t('cert.iso14001.desc')}</p>
            </div>

            {/* IATF 16949 */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 relative overflow-hidden hover:border-bronze-500/50 transition-all duration-300">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-bronze-500 to-olive-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="absolute top-4 right-4">
                <span className="px-2 py-1 bg-olive-500 text-charcoal-900 text-[10px] font-tactical font-bold tracking-wider">{t('cert.applied')}</span>
              </div>
              <div className="w-16 h-16 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mb-6">
                <i className="ri-car-line text-3xl text-bronze-500"></i>
              </div>
              <h3 className="text-xl font-display font-bold text-white mb-2">{t('cert.iatf')}</h3>
              <p className="text-sm text-charcoal-200 font-body">{t('cert.iatf.desc')}</p>
            </div>

            {/* CQI-12 */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 relative overflow-hidden hover:border-bronze-500/50 transition-all duration-300">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-bronze-500 to-olive-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              <div className="absolute top-4 right-4">
                <span className="px-2 py-1 bg-olive-500 text-charcoal-900 text-[10px] font-tactical font-bold tracking-wider">{t('cert.certified')}</span>
              </div>
              <div className="w-16 h-16 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mb-6">
                <i className="ri-file-shield-2-line text-3xl text-bronze-500"></i>
              </div>
              <h3 className="text-xl font-display font-bold text-white mb-2">{t('cert.cqi12')}</h3>
              <p className="text-sm text-charcoal-200 font-body">{t('cert.cqi12.desc')}</p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-charcoal-900 border border-charcoal-700 p-6 flex items-center space-x-4">
              <i className="ri-checkbox-circle-line text-2xl text-bronze-500"></i>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white">{t('cert.rohs')}</h4>
                <p className="text-xs text-charcoal-200">{t('cert.rohs.desc')}</p>
              </div>
            </div>
            <div className="bg-charcoal-900 border border-charcoal-700 p-6 flex items-center space-x-4">
              <i className="ri-checkbox-circle-line text-2xl text-bronze-500"></i>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white">{t('cert.reach')}</h4>
                <p className="text-xs text-charcoal-200">{t('cert.reach.desc')}</p>
              </div>
            </div>
            <div className="bg-charcoal-900 border border-charcoal-700 p-6 flex items-center space-x-4">
              <i className="ri-checkbox-circle-line text-2xl text-bronze-500"></i>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white">{t('cert.ca65')}</h4>
                <p className="text-xs text-charcoal-200">{t('cert.ca65.desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ESG Section */}
      <section id="esg" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/382a3f4e2dacfc6e9d3e92d1215e0f05.jpeg"
            alt="Metal Background"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-olive-500/30 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 mb-4 justify-center">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">{t('esg.subtitle')}</span>
              <div className="h-px w-12 bg-bronze-500"></div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('esg.title')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Environment */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-olive-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 bg-olive-500 flex items-center justify-center">
                  <i className="ri-plant-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('esg.environment')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.env1')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.env2')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.env3')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.env4')}</span></li>
              </ul>
            </div>

            {/* Social */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-olive-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 bg-olive-500 flex items-center justify-center">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('esg.social')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.soc1')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.soc2')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.soc3')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.soc4')}</span></li>
              </ul>
            </div>

            {/* Governance */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-olive-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-14 h-14 bg-olive-500 flex items-center justify-center">
                  <i className="ri-building-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('esg.governance')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.gov1')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.gov2')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.gov3')}</span></li>
                <li className="flex items-start space-x-2"><i className="ri-check-line text-olive-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('esg.gov4')}</span></li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-square bg-charcoal-800 border border-charcoal-700 flex flex-col items-center justify-center p-4 hover:border-bronze-500/50 transition-colors">
              <div className="text-4xl font-display font-bold text-bronze-500 mb-2">10%</div>
              <p className="text-xs font-tactical text-charcoal-200 tracking-wider text-center">{t('esg.stat1')}<br />{t('esg.stat1.year')}</p>
            </div>
            <div className="aspect-square bg-charcoal-800 border border-charcoal-700 flex flex-col items-center justify-center p-4 hover:border-bronze-500/50 transition-colors">
              <div className="text-4xl font-display font-bold text-bronze-500 mb-2">100%</div>
              <p className="text-xs font-tactical text-charcoal-200 tracking-wider text-center">{t('esg.stat2')}<br />{t('esg.stat2.sub')}</p>
            </div>
            <div className="aspect-square bg-charcoal-800 border border-charcoal-700 flex flex-col items-center justify-center p-4 hover:border-bronze-500/50 transition-colors">
              <div className="text-4xl font-display font-bold text-bronze-500 mb-2">95%</div>
              <p className="text-xs font-tactical text-charcoal-200 tracking-wider text-center">{t('esg.stat3')}<br />{t('esg.stat3.sub')}</p>
            </div>
            <div className="aspect-square bg-charcoal-800 border border-charcoal-700 flex flex-col items-center justify-center p-4 hover:border-bronze-500/50 transition-colors">
              <div className="text-4xl font-display font-bold text-bronze-500 mb-2">0</div>
              <p className="text-xs font-tactical text-charcoal-200 tracking-wider text-center">{t('esg.stat4')}<br />{t('esg.stat4.sub')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-charcoal-800 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-bronze-500/30 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 mb-6 justify-center">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">{t('services.subtitle')}</span>
              <div className="h-px w-12 bg-bronze-500"></div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('services.title')}</h2>
            <p className="text-charcoal-200 text-base max-w-3xl mx-auto font-body">{t('services.description')}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="group bg-charcoal-900 border border-charcoal-700 p-6 text-center hover:border-bronze-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mx-auto mb-4 group-hover:border-bronze-500/50 transition-colors">
                <i className="ri-car-line text-2xl text-white"></i>
              </div>
              <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('services.automotive')}</h4>
              <p className="text-xs text-charcoal-200">{t('services.automotive.desc')}</p>
            </div>

            <div className="group bg-charcoal-900 border border-charcoal-700 p-6 text-center hover:border-bronze-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mx-auto mb-4 group-hover:border-bronze-500/50 transition-colors">
                <i className="ri-tools-line text-2xl text-white"></i>
              </div>
              <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('services.precision')}</h4>
              <p className="text-xs text-charcoal-200">{t('services.precision.desc')}</p>
            </div>

            <div className="group bg-charcoal-900 border border-charcoal-700 p-6 text-center hover:border-bronze-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mx-auto mb-4 group-hover:border-bronze-500/50 transition-colors">
                <i className="ri-riding-line text-2xl text-white"></i>
              </div>
              <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('services.sports')}</h4>
              <p className="text-xs text-charcoal-200">{t('services.sports.desc')}</p>
            </div>

            <div className="group bg-charcoal-900 border border-charcoal-700 p-6 text-center hover:border-bronze-500/50 transition-all duration-300">
              <div className="w-14 h-14 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center mx-auto mb-4 group-hover:border-bronze-500/50 transition-colors">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('services.medical')}</h4>
              <p className="text-xs text-charcoal-200">{t('services.medical.desc')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Medical Devices */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 hover:border-bronze-500/50 transition-all duration-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-red-500 flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-display font-bold text-white">{t('services.medicalDevice')}</h3>
              </div>
              <p className="text-sm font-tactical font-semibold text-red-500 mb-3">{t('services.medicalDevice.title')}</p>
              <p className="text-sm text-charcoal-200 mb-6 font-body">{t('services.medicalDevice.desc')}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.medicalDevice.tag1')}</span>
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.medicalDevice.tag2')}</span>
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.medicalDevice.tag3')}</span>
              </div>
            </div>

            {/* Precision Molds */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 hover:border-olive-500/50 transition-all duration-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-olive-500 flex items-center justify-center">
                  <i className="ri-box-3-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-display font-bold text-white">{t('services.mold')}</h3>
              </div>
              <p className="text-sm font-tactical font-semibold text-olive-400 mb-3">{t('services.mold.title')}</p>
              <p className="text-sm text-charcoal-200 mb-6 font-body">{t('services.mold.desc')}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.mold.tag1')}</span>
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.mold.tag2')}</span>
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.mold.tag3')}</span>
              </div>
            </div>

            {/* Bicycles & Motorcycles */}
            <div className="group bg-charcoal-900 border border-charcoal-700 p-8 hover:border-bronze-500/50 transition-all duration-300">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-bronze-500 flex items-center justify-center">
                  <i className="ri-riding-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-display font-bold text-white">{t('services.bicycle')}</h3>
              </div>
              <p className="text-sm font-tactical font-semibold text-bronze-500 mb-3">{t('services.bicycle.title')}</p>
              <p className="text-sm text-charcoal-200 mb-6 font-body">{t('services.bicycle.desc')}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.bicycle.tag1')}</span>
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.bicycle.tag2')}</span>
                <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">{t('services.bicycle.tag3')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/382a3f4e2dacfc6e9d3e92d1215e0f05.jpeg"
            alt="Metal Background"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-bronze-500/30 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 mb-4 justify-center">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">{t('tech.subtitle')}</span>
              <div className="h-px w-12 bg-bronze-500"></div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('tech.title')}</h2>
            <p className="text-charcoal-200 text-base max-w-3xl mx-auto font-body">{t('tech.description')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Natural Finish */}
            <div className="bg-charcoal-800 border border-charcoal-700 overflow-hidden hover:border-olive-500/50 transition-all duration-300">
              <div className="p-8 md:p-10">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-charcoal-400 to-charcoal-500 flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-charcoal-900"></i>
                  </div>
                  <div>
                    <span className="text-xs font-tactical text-bronze-500 tracking-wider">TYPE III</span>
                    <h3 className="text-2xl font-display font-bold text-white">{t('tech.natural')}</h3>
                    <p className="text-sm text-charcoal-200 font-tactical">{t('tech.natural.sub')}</p>
                  </div>
                </div>

                <p className="text-sm text-charcoal-200 mb-8 leading-relaxed font-body">{t('tech.natural.desc')}</p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-bronze-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-microscope-line text-bronze-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-tactical font-semibold text-white mb-1">{t('tech.natural.feat1')}</h4>
                      <p className="text-xs text-charcoal-200">{t('tech.natural.feat1.desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-bronze-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-star-line text-bronze-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-tactical font-semibold text-white mb-1">{t('tech.natural.feat2')}</h4>
                      <p className="text-xs text-charcoal-200">{t('tech.natural.feat2.desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-bronze-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-ruler-2-line text-bronze-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-tactical font-semibold text-white mb-1">{t('tech.natural.feat3')}</h4>
                      <p className="text-xs text-charcoal-200">{t('tech.natural.feat3.desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-charcoal-700 text-charcoal-200 text-xs font-tactical border border-charcoal-600">AL 2000 Series</span>
                  <span className="px-3 py-1 bg-charcoal-700 text-charcoal-200 text-xs font-tactical border border-charcoal-600">AL 6000 Series</span>
                  <span className="px-3 py-1 bg-charcoal-700 text-charcoal-200 text-xs font-tactical border border-charcoal-600">AL 7000 Series</span>
                  <span className="px-3 py-1 bg-bronze-500/20 text-bronze-500 text-xs font-tactical border border-bronze-500/30">CQI-12</span>
                </div>
              </div>
            </div>

            {/* Hard Black Finish */}
            <div className="bg-gradient-to-br from-charcoal-900 to-black overflow-hidden hover:border-bronze-500/50 transition-all duration-300">
              <div className="p-8 md:p-10">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-charcoal-800 to-black border border-charcoal-600 flex items-center justify-center">
                    <i className="ri-contrast-2-line text-2xl text-charcoal-200"></i>
                  </div>
                  <div>
                    <span className="text-xs font-tactical text-bronze-500 tracking-wider">TYPE III</span>
                    <h3 className="text-2xl font-display font-bold text-white">{t('tech.black')}</h3>
                    <p className="text-sm text-charcoal-200 font-tactical">{t('tech.black.sub')}</p>
                  </div>
                </div>

                <p className="text-sm text-charcoal-200 mb-8 leading-relaxed font-body">{t('tech.black.desc')}</p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-bronze-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-shield-check-line text-bronze-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-tactical font-semibold text-white mb-1">{t('tech.black.feat1')}</h4>
                      <p className="text-xs text-charcoal-200">{t('tech.black.feat1.desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-bronze-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-palette-line text-bronze-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-tactical font-semibold text-white mb-1">{t('tech.black.feat2')}</h4>
                      <p className="text-xs text-charcoal-200">{t('tech.black.feat2.desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-bronze-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-sun-line text-bronze-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-tactical font-semibold text-white mb-1">{t('tech.black.feat3')}</h4>
                      <p className="text-xs text-charcoal-200">{t('tech.black.feat3.desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">Deep Black</span>
                  <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">UV Resistant</span>
                  <span className="px-3 py-1 bg-charcoal-800 text-charcoal-200 text-xs font-tactical border border-charcoal-600">Fade-Proof</span>
                  <span className="px-3 py-1 bg-bronze-500/20 text-bronze-500 text-xs font-tactical border border-bronze-500/30">CQI-12</span>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Note */}
          <div className="bg-charcoal-800 border-l-4 border-bronze-500 p-6">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-bronze-500/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-xl text-bronze-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white mb-2">{t('tech.note.title')}</h4>
                <p className="text-xs text-charcoal-200 leading-relaxed font-body">{t('tech.note.desc')}</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a href="#contact" className="inline-flex items-center space-x-2 px-8 py-4 bg-bronze-500 text-charcoal-900 text-sm font-tactical font-bold tracking-wider hover:bg-bronze-400 transition-all whitespace-nowrap cursor-pointer">
              <span>{t('tech.consult')}</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Product Features Section */}
      <section id="products" className="py-24 bg-charcoal-800 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-olive-500/30 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 mb-6 justify-center">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">{t('product.subtitle')}</span>
              <div className="h-px w-12 bg-bronze-500"></div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('product.title')}</h2>
            <p className="text-charcoal-200 text-base max-w-3xl mx-auto font-body">{t('product.description')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Hard Anodizing - Natural */}
            <div className="bg-charcoal-900 border border-charcoal-700 overflow-hidden hover:border-olive-500/50 transition-all duration-300">
              <div className="aspect-[4/3] bg-charcoal-700 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/2987e101065d27346dedfaf95abfae18.jpeg"
                  alt="Hard Anodizing Natural"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-charcoal-300 to-charcoal-400 flex items-center justify-center">
                    <i className="ri-shield-line text-xl text-charcoal-900"></i>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-white">{t('product.natural')}</h3>
                </div>
                <p className="text-sm text-charcoal-200 mb-6 leading-relaxed font-body">{t('product.natural.desc')}</p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.natural.feat1')}</span></li>
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.natural.feat2')}</span></li>
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.natural.feat3')}</span></li>
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.natural.feat4')}</span></li>
                </ul>
              </div>
            </div>

            {/* Hard Anodizing - Black */}
            <div className="bg-gradient-to-br from-charcoal-900 to-black border border-charcoal-700 overflow-hidden hover:border-bronze-500/50 transition-all duration-300">
              <div className="aspect-[4/3] bg-charcoal-800 overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/9f03e5ffe2e5ed8c54eb29a9957e8937.jpeg"
                  alt="Hard Anodizing Black"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-charcoal-600 to-black border border-charcoal-500 flex items-center justify-center">
                    <i className="ri-shield-star-line text-xl text-charcoal-200"></i>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-white">{t('product.black')}</h3>
                </div>
                <p className="text-sm text-charcoal-200 mb-6 leading-relaxed font-body">{t('product.black.desc')}</p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.black.feat1')}</span></li>
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.black.feat2')}</span></li>
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.black.feat3')}</span></li>
                  <li className="flex items-start space-x-3"><i className="ri-check-double-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('product.black.feat4')}</span></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a href="#contact" className="inline-flex items-center space-x-2 px-8 py-4 bg-olive-500 text-charcoal-900 text-sm font-tactical font-bold tracking-wider hover:bg-olive-400 transition-all whitespace-nowrap cursor-pointer">
              <span>{t('product.inquire')}</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section id="compliance" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/382a3f4e2dacfc6e9d3e92d1215e0f05.jpeg"
            alt="Metal Background"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-bronze-500/30 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-3 mb-4 justify-center">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">{t('compliance.subtitle')}</span>
              <div className="h-px w-12 bg-bronze-500"></div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('compliance.title')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {/* Quality Standards */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-bronze-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-bronze-500 flex items-center justify-center">
                  <i className="ri-award-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('compliance.quality')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.quality1')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.quality2')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.quality3')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.quality4')}</span></li>
              </ul>
            </div>

            {/* Environmental Standards */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-bronze-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-bronze-500 flex items-center justify-center">
                  <i className="ri-leaf-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('compliance.environment')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.env1')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.env2')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.env3')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.env4')}</span></li>
              </ul>
            </div>

            {/* International Regulations */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-bronze-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-bronze-500 flex items-center justify-center">
                  <i className="ri-global-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('compliance.international')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.int1')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.int2')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.int3')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.int4')}</span></li>
              </ul>
            </div>

            {/* Social Responsibility */}
            <div className="bg-charcoal-800 border border-charcoal-700 p-8 hover:border-bronze-500/50 transition-colors">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-bronze-500 flex items-center justify-center">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-display font-bold text-white">{t('compliance.social')}</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.soc1')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.soc2')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.soc3')}</span></li>
                <li className="flex items-start space-x-3"><i className="ri-checkbox-circle-line text-bronze-500 mt-0.5"></i><span className="text-sm text-charcoal-200">{t('compliance.soc4')}</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=high-precision%20electroplated%20metal%20components%20and%20parts%20displayed%20on%20a%20clean%20industrial%20surface%20with%20blue%20ambient%20lighting%20showcasing%20perfect%20metallic%20finish%20and%20quality%20craftsmanship%20in%20a%20modern%20manufacturing%20environment%20representing%20excellence%20in%20surface%20treatment%20technology&width=1920&height=800&seq=cta-bg-001&orientation=landscape"
            alt="Manufacturing Facility"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal-900/98 via-charcoal-900/90 to-charcoal-900/75"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center space-x-3 mb-6">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">PARTNERSHIP</span>
            </div>
            
            <h2 className="font-display text-5xl md:text-7xl font-bold text-white mb-6 leading-none">
              <span className="block">{t('cta.title1')}</span>
              <span className="block text-bronze-500">{t('cta.title2')}</span>
            </h2>
            
            <p className="text-xl text-charcoal-200 mb-10 font-body">{t('cta.description')}</p>
            
            <a
              href="#contact"
              className="group inline-flex items-center space-x-4 bg-bronze-500 text-charcoal-900 font-tactical font-bold tracking-wider hover:bg-bronze-400 transition-all cursor-pointer px-8 py-4"
            >
              <span className="text-sm whitespace-nowrap">{t('cta.button')}</span>
              <i className="ri-arrow-right-up-line text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-charcoal-800 relative">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/abf81f922f6f00780f8855c03cb98972.jpeg"
            alt="Contact Background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-charcoal-900/90"></div>
        </div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-bronze-500/30 to-transparent"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-3 mb-6 justify-center">
              <div className="h-px w-12 bg-bronze-500"></div>
              <span className="text-bronze-500 text-xs font-tactical font-semibold tracking-[0.3em]">CONTACT</span>
              <div className="h-px w-12 bg-bronze-500"></div>
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">{t('contact.title')}</h2>
            <p className="text-charcoal-200 text-base font-body">{t('contact.description')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <div className="bg-charcoal-900 border border-charcoal-700 p-5 flex items-center space-x-4 hover:border-bronze-500/50 transition-colors">
              <div className="w-12 h-12 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-phone-line text-xl text-bronze-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('contact.phone')}</h4>
                <p className="text-sm text-charcoal-200">02-8201-0771</p>
              </div>
            </div>

            <div className="bg-charcoal-900 border border-charcoal-700 p-5 flex items-center space-x-4 hover:border-olive-500/50 transition-colors">
              <div className="w-12 h-12 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-xl text-bronze-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('contact.email')}</h4>
                <p className="text-sm text-charcoal-200 break-all">Hy.coltd@msa.hinet.net</p>
              </div>
            </div>

            <div className="bg-charcoal-900 border border-charcoal-700 p-5 flex items-center space-x-4 hover:border-bronze-500/50 transition-colors">
              <div className="w-12 h-12 bg-charcoal-800 border border-charcoal-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-map-pin-line text-xl text-bronze-500"></i>
              </div>
              <div>
                <h4 className="text-sm font-tactical font-bold text-white mb-1">{t('contact.address')}</h4>
                <p className="text-sm text-charcoal-200 leading-relaxed">{t('contact.addressValue')}</p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <a
              href="https://www.google.com/maps/search/?api=1&query=No.+35-1,+Ln.+268,+Xinshu+Rd.,+Xinzhuang+Dist.,+New+Taipei+City+242054,+Taiwan+(R.O.C.)"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-3 bg-charcoal-900 border border-charcoal-600 text-charcoal-200 font-tactical font-medium tracking-wider hover:border-bronze-500 hover:text-bronze-500 transition-all whitespace-nowrap cursor-pointer px-6 py-3"
            >
              <i className="ri-map-pin-2-line text-xl"></i>
              <span className="text-sm whitespace-nowrap">{t('contact.openMap')}</span>
              <i className="ri-external-link-line text-lg"></i>
            </a>
          </div>

          <div className="bg-charcoal-900 border border-charcoal-700 p-8 md:p-10">
            <form className="space-y-6" onSubmit={handleSubscribe}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-tactical font-medium text-charcoal-200 mb-2">{t('contact.form.name')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 text-sm text-white placeholder-charcoal-400 focus:outline-none focus:border-bronze-500 transition-colors font-body"
                    placeholder={t('contact.form.namePlaceholder')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-tactical font-medium text-charcoal-200 mb-2">{t('contact.form.company')}</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 text-sm text-white placeholder-charcoal-400 focus:outline-none focus:border-bronze-500 transition-colors font-body"
                    placeholder={t('contact.form.companyPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-tactical font-medium text-charcoal-200 mb-2">{t('contact.form.email')}</label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 text-sm text-white placeholder-charcoal-400 focus:outline-none focus:border-bronze-500 transition-colors font-body"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-tactical font-medium text-charcoal-200 mb-2">{t('contact.form.phone')}</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 text-sm text-white placeholder-charcoal-400 focus:outline-none focus:border-bronze-500 transition-colors font-body"
                    placeholder="+886"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-tactical font-medium text-charcoal-200 mb-2">{t('contact.form.message')}</label>
                <textarea
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-charcoal-800 border border-charcoal-600 text-sm text-white placeholder-charcoal-400 focus:outline-none focus:border-bronze-500 transition-colors resize-none font-body"
                  placeholder={t('contact.form.messagePlaceholder')}
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-bronze-500 text-charcoal-900 text-sm font-tactical font-bold tracking-wider hover:bg-bronze-400 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('contact.form.submit')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-charcoal-700 py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/acea91c8c3ad777d9c103c94efe1e40c/382a3f4e2dacfc6e9d3e92d1215e0f05.jpeg"
            alt="Metal Background"
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-12 mb-16">
            {/* Company Info */}
            <div className="lg:col-span-4">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-olive-500 to-olive-700 rounded flex items-center justify-center">
                  <span className="text-white font-tactical font-bold text-xl">HY</span>
                </div>
                <div>
                  <h3 className="text-xl font-display font-bold text-white">{t('footer.companyName')}</h3>
                  <div className="h-0.5 w-full bg-gradient-to-r from-bronze-500 to-transparent mt-1"></div>
                </div>
              </div>
              <p className="text-sm text-charcoal-300 mb-1 font-tactical">{t('footer.companyNameEn')}</p>
              <p className="text-sm text-charcoal-400 mb-8 font-body leading-relaxed">{t('footer.tagline')}</p>

              <div>
                <label className="block text-sm font-tactical font-medium text-charcoal-200 mb-2">{t('footer.newsletter')}</label>
                <form onSubmit={handleSubscribe} className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-4 py-3 bg-charcoal-800 border border-charcoal-600 text-sm text-white placeholder-charcoal-400 focus:outline-none focus:border-bronze-500 font-body rounded"
                    required
                  />
                  <button
                    type="submit"
                    className="px-5 py-3 bg-olive-500 text-charcoal-900 text-sm font-tactical font-bold hover:bg-olive-400 transition-all whitespace-nowrap cursor-pointer rounded"
                  >
                    {t('footer.subscribe')}
                  </button>
                </form>
                {formStatus === 'success' && (
                  <p className="text-xs text-olive-500 mt-2 font-tactical">Subscribed successfully!</p>
                )}
              </div>
            </div>

            {/* Navigation Links */}
            <div className="lg:col-span-2 lg:col-start-6">
              <h4 className="text-sm font-tactical font-bold text-white mb-5 tracking-wider">{t('footer.aboutUs')}</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">{t('nav.about')}</a></li>
                <li><a href="#technology" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">{t('nav.technology')}</a></li>
                <li><a href="#services" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">{t('nav.aesthetics')}</a></li>
                <li><a href="#contact" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">{t('nav.contact')}</a></li>
              </ul>
            </div>

            <div className="lg:col-span-2">
              <h4 className="text-sm font-tactical font-bold text-white mb-5 tracking-wider">{t('footer.coreTech')}</h4>
              <ul className="space-y-3">
                <li><a href="#technology" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">TYPE III</a></li>
                <li><a href="#products" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">Matte Finish</a></li>
                <li><a href="#products" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">Polished Finish</a></li>
                <li><a href="#compliance" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">Quality Management</a></li>
              </ul>
            </div>

            <div className="lg:col-span-2">
              <h4 className="text-sm font-tactical font-bold text-white mb-5 tracking-wider">{t('footer.certifications')}</h4>
              <ul className="space-y-3">
                <li><a href="#certifications" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">ISO 9001</a></li>
                <li><a href="#certifications" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">ISO 14001</a></li>
                <li><a href="#certifications" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">CQI-12</a></li>
                <li><a href="#certifications" className="text-sm text-charcoal-300 hover:text-bronze-500 transition-colors cursor-pointer font-body">RoHS / REACH</a></li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-charcoal-700/50 pt-10">
            <div className="flex flex-col md:flex-row justify-center items-center gap-4">
              <p className="text-sm text-charcoal-400 font-body">{t('footer.copyright')}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
